import { v4 } from 'uuid'
import wx from 'weixin-js-sdk'
import { isInWeChat } from './env'
import { bridge, BRIDGE_METHODS } from '@h5-journey/bridge'

/**
 * 防抖函数
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number = 500
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  return function (this: unknown, ...args: Parameters<T>) {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

/**
 * px转vw
 * @param px
 * @param designWidth 设计稿宽度 默认375px
 * @returns vw值
 */
export const px2vw = (px: number, designWidth = 375) => {
  return (px / designWidth) * 100 + 'vw'
}

/**
 * 节流函数
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let last = 0
  let timeout: ReturnType<typeof setTimeout> | null = null

  const throttled = function (this: unknown, ...args: Parameters<T>) {
    const now = Date.now()
    if (now - last >= delay) {
      last = now
      fn.apply(this, args)
    }
  }

  throttled.cancel = function () {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
    last = 0
  }

  return throttled as ((...args: Parameters<T>) => void) & { cancel: () => void }
}

/**
 * 深拷贝
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => deepClone(item)) as T
  }
  const cloned = {} as T
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = deepClone(obj[key])
    }
  }
  return cloned
}

/**
 * 生成唯一ID
 */
export function generateId(prefix = ''): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 9)}`
}

/** 选项数组项类型 */
export interface LabelValueItem {
  label: string
  value: string | number
}

/**
 * 将选项数组转换为对象映射
 * @param arr - 选项数组
 * @returns 以 value 为 key，label 为 value 的对象
 */
export function toJson(arr: LabelValueItem[]): Record<string | number, string> {
  const result: Record<string | number, string> = {}
  for (const item of arr) {
    result[item.value] = item.label
  }
  return result
}

/**
 * 复制内容到剪贴板
 * @param content - 要复制的内容
 */
export async function copyToClipboard(content: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(content)
  } catch (err) {
    console.error('复制失败', err)
  }
}

export const TERMINAL_MAP = {
  cloudQuickPayMini: '0', // 云闪付小程序
  wechatMini: '1', // 微信小程序
  normalBrowser: '2', // 普通浏览器
  app: '3', // 中旅APP
  tiktok: '4', // 抖音小程序
  bestPay: '5', // 翼支付App
  ctsPay: '6' // 中旅通
}

/** 推广码映射 */
export const CREATOR_PROMOTION_MAP: Record<string, string> = {
  [TERMINAL_MAP.cloudQuickPayMini]: '9ngu',
  [TERMINAL_MAP.bestPay]: 'ug9p',
  [TERMINAL_MAP.wechatMini]: '',
  [TERMINAL_MAP.tiktok]: 'lp4z',
  [TERMINAL_MAP.normalBrowser]: '',
}

// 获取使用的jssdk类型 如 微信sdk、字节sdk
export function getSdkFn() {
  const agent = navigator.userAgent.toLowerCase()
  if (agent.indexOf('toutiaomicroapp') > -1) {
    return (window as any).tt
  } else {
    const terminal = sessionStorage.getItem('terminal')
    // 浏览器环境重写miniProgram路由方法
    if (terminal === TERMINAL_MAP.ctsPay) {
      const apiList = ['navigateTo', 'redirectTo', 'switchTab']
      apiList.forEach((key) => {
        //@ts-ignore
        wx.miniProgram[key] = (params: unknown) => {
          console.log('params', params)
          window.top?.postMessage(
            {
              router: {
                type: key,
                params
              }
            },
            '*'
          )
        }
      })
    }
    return wx
  }
}

export function getUniqueId(name: string = 'track-uuid') {
  let id = localStorage.getItem(name)
  if (!id) {
    id = v4()
    localStorage.setItem(name, id)
  }
  return id
}

/**
 * 数字脱敏
 * 包含：手机、证件号、银行卡号
 * @param inputString
 * @returns 脱敏后的字符串
 */
export function desensitizeNumber(inputString: string): string {
  if (typeof inputString !== 'string') {
    return inputString as unknown as string
  }

  if (inputString.length <= 7) {
    return inputString
  }

  const frontPart = inputString.slice(0, 3)
  const backPart = inputString.slice(-4)
  const middlePart = '****'

  return frontPart + middlePart + backPart
}

/**
 * 地址脱敏
 * @param address
 * @returns 脱敏后的地址
 */
export function desensitizeAddress(address: string): string {
  if (typeof address !== 'string') {
    return address as unknown as string
  }

  const ADDRESS_MASK_FLAG = '市'
  const index = address.indexOf(ADDRESS_MASK_FLAG)
  if (index === -1) {
    return address
  }

  const desensitizedAddress =
    address.slice(0, index + 1) + (address.slice(index + 1).length > 0 ? '****' : '')
  return desensitizedAddress
}

/**
 * 数据整体脱敏
 * @param inputString
 * @returns 脱敏后的字符串
 */
export function desensitizeAll(inputString: string): string {
  if (inputString) {
    return '****'
  }
  return ''
}

export async function linkToIndex() {
  if (isInWeChat()) {
    getSdkFn()?.miniProgram?.switchTab({
      url: `/pages/index/index`,
    })
    return
  }
  bridge.invokeSync(BRIDGE_METHODS.GO_HOME)
}

// 判断当前环境是ios还是android
export const getPlantFormType = () => {
  const u = navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1
  const isiOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
  if (isAndroid) {
    return 'android'
  } else if (isiOS) {
    return 'ios'
  }
  return ''
}
